import React, { useState, useEffect } from 'react';
import {
  Eye, Edit, Trash2, Calendar, DollarSign, Building2, Briefcase,
  AlertTriangle, CheckCircle, XCircle, Clock
} from 'lucide-react';
import { getEmployeePhotoUrl } from '../../../utils/imageUtils';
import Pagination from '../../common/Pagination';

const POR_PAGINA = 12;

const getTipoLabel = (tipo) => {
  const labels = {
    indefinido: 'Indefinido',
    plazo_fijo: 'Plazo Fijo',
    temporal:   'Temporal',
    por_obra:   'Por Obra',
    prueba:     'Período de Prueba',
  };
  return labels[tipo] || tipo;
};

const getTipoCls = (tipo) => {
  const map = {
    indefinido: 'bg-green-50 text-green-700',
    plazo_fijo: 'bg-amber-50 text-amber-700',
    temporal:   'bg-orange-50 text-orange-700',
    por_obra:   'bg-gray-100 text-gray-700',
    prueba:     'bg-gray-100 text-gray-600',
  };
  return map[tipo] || 'bg-gray-100 text-gray-600';
};

const getEstadoBadge = (estado) => {
  const badges = {
    activo:     { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'Activo' },
    finalizado: { bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400',  label: 'Finalizado' },
    rescindido: { bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500',   label: 'Rescindido' },
    suspendido: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Suspendido' },
  };
  return badges[estado] || badges.finalizado;
};

const ContractsList = ({ contracts, onView, onEdit, onDelete }) => {
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => { setPaginaActual(1); }, [contracts]);

  if (contracts.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
        <Briefcase size={40} className="text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500">No se encontraron contratos</p>
        <p className="text-xs text-gray-400 mt-1">Ajusta los filtros o crea un nuevo contrato</p>
      </div>
    );
  }

  const totalPaginas  = Math.ceil(contracts.length / POR_PAGINA);
  const inicio        = (paginaActual - 1) * POR_PAGINA;
  const pagina        = contracts.slice(inicio, inicio + POR_PAGINA);


  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">

          {/* ── Head ─────────────────────────────────────────────── */}
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-10 px-4 py-3">
                <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" disabled />
              </th>
              {['EMPLEADO', 'TIPO', 'CARGO / DPTO', 'SALARIO', 'VIGENCIA', 'ESTADO', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ─────────────────────────────────────────────── */}
          <tbody className="divide-y divide-gray-100">
            {pagina.map((contract) => {
              const badge             = getEstadoBadge(contract.estado);
              const diasRestantes     = contract.dias_restantes;
              const proximoVencer     = diasRestantes !== null && diasRestantes <= 30 && diasRestantes >= 0;
              const initials          = contract.empleado_nombre?.[0]?.toUpperCase() || '?';

              return (
                <tr
                  key={contract.id}
                  className={`hover:bg-gray-50 transition-colors cursor-pointer group ${proximoVencer ? 'bg-amber-50/50 hover:bg-amber-50' : ''}`}
                  onClick={() => onView(contract)}
                >
                  {/* Checkbox */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
                  </td>

                  {/* Empleado */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {getEmployeePhotoUrl(contract.empleado_foto) ? (
                          <img
                            src={getEmployeePhotoUrl(contract.empleado_foto)}
                            alt={contract.empleado_nombre}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div
                          className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ display: getEmployeePhotoUrl(contract.empleado_foto) ? 'none' : 'flex' }}
                        >
                          {initials}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{contract.empleado_nombre}</p>
                        <p className="text-xs text-gray-400">{contract.empleado_codigo}</p>
                      </div>
                    </div>
                  </td>

                  {/* Tipo */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoCls(contract.tipo_contrato)}`}>
                      {getTipoLabel(contract.tipo_contrato)}
                    </span>
                  </td>

                  {/* Cargo / Dpto */}
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">{contract.cargo || '—'}</p>
                    {contract.departamento && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <Building2 size={10} />
                        {contract.departamento}
                      </p>
                    )}
                  </td>

                  {/* Salario */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {contract.moneda || 'RD$'} {parseFloat(contract.salario || 0).toLocaleString('es-DO', { minimumFractionDigits: 0 })}
                    </span>
                  </td>

                  {/* Vigencia */}
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-600">
                      {new Date(contract.fecha_inicio + 'T12:00:00').toLocaleDateString('es-DO')}
                    </p>
                    {contract.fecha_fin && (
                      <p className="text-xs text-gray-400">
                        hasta {new Date(contract.fecha_fin + 'T12:00:00').toLocaleDateString('es-DO')}
                      </p>
                    )}
                    {proximoVencer && (
                      <p className="text-xs font-semibold text-amber-600 flex items-center gap-1 mt-0.5">
                        <AlertTriangle size={10} />
                        {diasRestantes}d
                      </p>
                    )}
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                      {badge.label}
                    </span>
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onView(contract)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Ver detalles"
                      >
                        <Eye size={14} />
                      </button>
                      {contract.estado === 'activo' && (
                        <>
                          <button
                            onClick={() => onEdit(contract)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => onDelete(contract)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Rescindir"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={paginaActual}
        totalPages={totalPaginas}
        total={contracts.length}
        pageSize={POR_PAGINA}
        onPageChange={setPaginaActual}
      />
    </div>
  );
};

export default ContractsList;
